import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Box, Chip, InputAdornment, Skeleton, TextField, Tooltip, Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { Icon, IconifyIcon } from '@iconify/react';
import feathered from '@iconify-icons/game-icons/feathered-wing';
import robot from '@iconify-icons/fluent-emoji-flat/robot';
import mercadoPago from '@iconify-icons/simple-icons/mercadopago';
import chatwoot from '@iconify-icons/simple-icons/chatwoot';
import link from '@iconify-icons/fluent-emoji-flat/link';
import incomingEnvelope from '@iconify-icons/fluent-emoji-flat/incoming-envelope';
import telephoneReceiver from '@iconify-icons/fluent-emoji-flat/telephone-receiver';
import bell from '@iconify-icons/fluent-emoji-flat/bell';
import megaphone from '@iconify-icons/fluent-emoji-flat/megaphone';
import fountainPen from '@iconify-icons/fluent-emoji-flat/fountain-pen';
import officeBuilding from '@iconify-icons/fluent-emoji-flat/office-building';
import mailbox from '@iconify-icons/fluent-emoji-flat/closed-mailbox-with-raised-flag';
import barChart from '@iconify-icons/fluent-emoji-flat/bar-chart';
import hook from '@iconify-icons/fluent-emoji-flat/hook';
import gear from '@iconify-icons/fluent-emoji-flat/gear';
import googleCalendar from '@iconify-icons/logos/google-calendar-2020';
import googleAds from '@iconify-icons/logos/google-ads';
import googleMaps from '@iconify-icons/logos/google-maps';
import facebook from '@iconify-icons/logos/facebook';
import metaIcon from '@iconify-icons/logos/meta-icon';
import whatsappIcon from '@iconify-icons/logos/whatsapp-icon';
import stripe from '@iconify-icons/logos/stripe';
import ExtensionIcon from '@mui/icons-material/Extension';
import DashboardLayout from '@examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from '@examples/Navbars/DashboardNavbar';
import MDButton from '@components/MDButton';
import { useMaterialUIController } from 'context';
import { useAlert } from '@components/showAlert/useAlertHook';
import { useTranslation } from 'react-i18next';
import { getCrmColors } from '@layouts/pages/CRM/kanban-board/crm-theme';
import { EIntegrationKind, Integration, IntegrationService } from './services/integration.service';

/// Integrações nativas com tela própria; as outras funcionam dentro de outro cadastro.
const NATIVE_PAGES: Record<string, string> = {
    'spreadsheet-import': '/crm/pessoas/importar',
    // Cada vendedor conecta a própria conta na Agenda: é lá que se vê se está conectada, não aqui.
    'google-calendar': '/crm/agenda',
};

/// Mesmo tamanho dos ícones do MUI; a cor da marca lê bem nos dois temas.
const ICON_HEIGHT = 24;

/// A asa do Asaas é de uma cor só e a marca não tem logo no pacote.
const WING_COLOR = '#FFB02E';

/// A largura acompanha a proporção de cada ícone.
const icon = (data: IconifyIcon, color?: string) => <Icon icon={data} height={ICON_HEIGHT} color={color} />;

/// Logo oficial quando o pacote tem (Chatwoot e Mercado Pago na cor da marca); sem logo, um emoji.
const ICONS: Record<string, JSX.Element> = {
    chatwoot: icon(chatwoot, '#1F93FF'),
    meta: icon(facebook),
    'meta-ads': icon(metaIcon),
    'meta-lead-ads': icon(facebook),
    'meta-whatsapp': icon(whatsappIcon),
    'google-calendar': icon(googleCalendar),
    'google-ads': icon(googleAds),
    'google-maps': icon(googleMaps),
    'mercado-pago': icon(mercadoPago, '#00B1EA'),
    stripe: icon(stripe),
    'api-webhooks': icon(link),
    'team-email': icon(incomingEnvelope),
    telephony: icon(telephoneReceiver),
    'team-chat': icon(bell),
    'rd-station': icon(megaphone),
    asaas: icon(feathered, WING_COLOR),
    'e-signature': icon(fountainPen),
    'cnpj-lookup': icon(officeBuilding),
    'postal-code': icon(mailbox),
    'spreadsheet-import': icon(barChart),
    ai: icon(robot),
    'outbound-webhooks': icon(hook),
    'automation-hub': icon(gear),
};

const ALL = '__todas__';

/// Fora da tela por decisão de produto; o backend continua. Sem o card ninguém configura,
/// e o que depende delas no lead (cobrança, e-mail em cópia, assinatura) já some sozinho.
const META_GROUP = 'meta';
const META_ROUTE = '/configuracoes/integracoes/meta';
const API_GROUP = 'api';
const API_ROUTE = '/configuracoes/integracoes/api';
const CHAT_ROUTE = '/configuracoes/integracoes/chat';
const CHATWOOT_ROUTE = '/configuracoes/integracoes/chatwoot';

/// Integrações com configuração em tela própria, fora dos grupos.
const FORM_PAGES: Record<string, string> = {
    'team-chat': CHAT_ROUTE,
    chatwoot: CHATWOOT_ROUTE,
};

const HIDDEN_KEYS = ['team-email', 'telephony', 'rd-station', 'asaas', 'mercado-pago', 'stripe', 'e-signature'];

export const Integrations = () => {
    const { t } = useTranslation();
    const [controller] = useMaterialUIController();
    const cc = getCrmColors(controller.darkMode);
    const { showAlert } = useAlert();
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();

    const [list, setList] = useState<Integration[] | null>(null);
    const [searchTerm, setSearch] = useState('');
    const [category, setCategory] = useState(ALL);

    const load = () =>
        new IntegrationService()
            .list()
            .then((res) => setList((res.data.data ?? []).filter((item) => !HIDDEN_KEYS.includes(item.key))))
            .catch(() => setList([]));

    useEffect(() => {
        load();
    }, []);

    // A Meta devolve o navegador para cá após o consentimento; o resultado vem na URL.
    useEffect(() => {
        const result = searchParams.get('meta');

        if (!result) return;

        const detail = searchParams.get('detalhe') ?? '';

        if (result === 'ok') {
            showAlert(t('integracoes.conectouMeta', { conta: detail }), 'success', 5000);
            load();
        } else {
            showAlert(detail || t('integracoes.falhouMeta'), 'error', 6000);
        }

        searchParams.delete('meta');
        searchParams.delete('detalhe');
        setSearchParams(searchParams, { replace: true });
    }, []);

    // O mesmo para Mercado Pago, Stripe e RD Station, que voltam com a integração em "conexao".
    useEffect(() => {
        const key = searchParams.get('conexao');

        if (!key) return;

        const detail = searchParams.get('detalhe') ?? '';

        if (searchParams.get('resultado') === 'ok') {
            showAlert(t('integracoes.conectouConta', { conta: detail }), 'success', 5000);
            load();
        } else {
            showAlert(detail || t('integracoes.falhouConta'), 'error', 6000);
        }

        ['conexao', 'resultado', 'detalhe'].forEach((param) => searchParams.delete(param));
        setSearchParams(searchParams, { replace: true });
    }, []);

    const configure = (items: Integration[]) => {
        const item = items[0];

        // A Meta tem tela própria: uma conta no topo e uma aba por integração que a usa.
        if (item.groupKey === META_GROUP) {
            navigate(META_ROUTE);
            return;
        }

        // API, webhooks e n8n/Zapier/Make: o card do grupo abre nos webhooks; o link direto à chave, nos tokens.
        if (item.groupKey === API_GROUP) {
            navigate(`${API_ROUTE}?aba=${items.length === 1 && item.key === 'api-webhooks' ? 'tokens' : 'webhooks'}`);
            return;
        }

        if (FORM_PAGES[item.key]) navigate(FORM_PAGES[item.key]);
    };

    // Outra tela manda a uma integração com ?abrir=<chave>; a de grupo abre o card na aba dela.
    useEffect(() => {
        const key = searchParams.get('abrir');

        if (!list || !key) return;

        const item = list.find((row) => row.key === key);

        // Troca a URL inteira: limpar o ?abrir depois traria a navegação de volta para cá.
        if (item?.groupKey === META_GROUP) {
            navigate(`${META_ROUTE}?aba=${item.key}`, { replace: true });
            return;
        }

        // Limpa antes: se a integração tiver tela própria, a navegação dela vem por último e vale.
        searchParams.delete('abrir');
        setSearchParams(searchParams, { replace: true });

        if (item) configure([item]);
    }, [list]);

    const categories = useMemo(
        () => [ALL, ...Array.from(new Set((list ?? []).map((item) => item.category)))],
        [list]
    );

    const visible = (list ?? []).filter((item) => {
        const term = searchTerm.trim().toLowerCase();
        const matchesSearch = !term
            || item.name.toLowerCase().includes(term)
            || item.description.toLowerCase().includes(term);

        return matchesSearch && (category === ALL || item.category === category);
    });

    const categoryLabel = (value: string) =>
        value === ALL ? t('integracoes.todas') : value;

    /// Integrações do mesmo fornecedor viram um card só: três cards pareciam três conexões.
    const cards = useMemo(() => {
        const outgoing: { key: string; items: Integration[] }[] = [];

        visible.forEach((item) => {
            if (!item.groupKey) {
                outgoing.push({ key: item.key, items: [item] });
                return;
            }

            const matchingGroup = outgoing.find((row) => row.key === item.groupKey);

            if (matchingGroup) matchingGroup.items.push(item);
            else outgoing.push({ key: item.groupKey, items: [item] });
        });

        return outgoing;
    }, [visible]);

    /// O card do grupo resume as três: configurada se todas as obrigatórias estão; ativa se alguma entrega.
    const summarize = (items: Integration[]): Integration => {
        if (items.length === 1) return items[0];

        const base = items[0];

        return {
            ...base,
            key: base.groupKey!,
            name: base.groupName ?? base.name,
            description: base.groupDescription ?? base.description,
            configured: items.every((item) => item.configured),
            enabled: items.some((item) => item.enabled),
            connectedAt: items.find((item) => item.connectedAt)?.connectedAt ?? null,
        };
    };

    return (
        <DashboardLayout>
            <DashboardNavbar title={t('integracoes.titulo')} />

            <Box sx={{ background: cc.pageBg, borderRadius: 3, p: 2 }}>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 2,
                        flexWrap: 'wrap',
                        mb: 2,
                    }}
                >
                    <Typography variant="caption" sx={{ color: cc.textSecondary }}>
                        {t('integracoes.explicacao')}
                    </Typography>

                    <TextField
                        size="small"
                        value={searchTerm}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder={t('integracoes.buscar')}
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ fontSize: 17, color: cc.textSecondary }} />
                                    </InputAdornment>
                                ),
                            },
                        }}
                        sx={{ width: { xs: '100%', sm: 280 } }}
                    />
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '190px 1fr' }, gap: 2 }}>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'row', md: 'column' }, gap: 0.5, flexWrap: 'wrap' }}>
                        {categories.map((item) => {
                            const active = category === item;

                            return (
                                <Box
                                    key={item}
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => setCategory(item)}
                                    onKeyDown={(e) => e.key === 'Enter' && setCategory(item)}
                                    sx={{
                                        px: 1.5,
                                        py: 1,
                                        borderRadius: '9px',
                                        cursor: 'pointer',
                                        fontSize: '0.85rem',
                                        fontWeight: active ? 700 : 500,
                                        color: active ? cc.textPrimary : cc.textSecondary,
                                        bgcolor: active ? cc.countBg : 'transparent',
                                        '&:hover': { bgcolor: cc.countBg },
                                    }}
                                >
                                    {categoryLabel(item)}
                                </Box>
                            );
                        })}
                    </Box>

                    <Box
                        sx={{
                            display: 'grid',
                            gap: 2,
                            gridTemplateColumns: {
                                xs: '1fr',
                                sm: 'repeat(2, 1fr)',
                                xl: 'repeat(3, 1fr)',
                            },
                            alignContent: 'start',
                        }}
                    >
                        {!list
                            ? Array.from({ length: 4 }).map((_, i) => (
                                <Skeleton key={i} variant="rounded" height={180} />
                            ))
                            : cards.length === 0
                            ? (
                                <Typography variant="body2" sx={{ color: cc.textSecondary, gridColumn: '1 / -1', py: 4, textAlign: 'center' }}>
                                    {t('integracoes.nenhumaEncontrada')}
                                </Typography>
                            )
                            : cards.map((row) => (
                                <IntegrationCard
                                    key={row.key}
                                    item={summarize(row.items)}
                                    cc={cc}
                                    t={t}
                                    onConfigure={() => configure(row.items)}
                                />
                            ))}
                    </Box>
                </Box>
            </Box>
        </DashboardLayout>
    );
};

interface CardProps {
    item: Integration;
    cc: ReturnType<typeof getCrmColors>;
    t: (key: string) => string;
    onConfigure: () => void;
}

const IntegrationCard = ({ item, cc, t, onConfigure }: CardProps) => {
    const navigate = useNavigate();
    const unavailable = item.kind === EIntegrationKind.NotAvailable;
    const native = item.kind === EIntegrationKind.Native;
    // Existe, mas falta credencial que a mgcode põe no servidor: nada que o cliente configure.
    const needsSupport = !unavailable && !item.serverConfigured;

    return (
        <Box
            sx={{
                background: cc.cardBg,
                border: `1px solid ${cc.border}`,
                borderRadius: 3,
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                position: 'relative',
                opacity: unavailable || needsSupport ? 0.6 : 1,
            }}
        >
            {/* Na nativa, "configurada" só significa "existe neste servidor", como o card já diz. */}
            {item.configured && !native && (
                <Chip
                    label={t('integracoes.configurada')}
                    size="small"
                    color="success"
                    variant="outlined"
                    sx={{ position: 'absolute', top: 12, right: 12, height: 20, fontSize: '10px', '& .MuiChip-label': { color: cc.onTint } }}
                />
            )}

            <Box sx={{ color: cc.accent, display: 'flex' }}>
                {ICONS[item.key] ?? <ExtensionIcon />}
            </Box>

            <Typography variant="body2" fontWeight={700} sx={{ color: cc.textPrimary }}>
                {item.name}
            </Typography>

            <Typography variant="caption" sx={{ color: cc.textSecondary, flex: 1 }}>
                {item.description}
            </Typography>

            {item.accountLabel && (
                <Typography variant="caption" sx={{ color: cc.textMuted, fontSize: '10.5px' }}>
                    {item.accountLabel}
                </Typography>
            )}

            <Box sx={{ mt: 0.5 }}>
                {needsSupport ? (
                    <Tooltip title={t('integracoes.faltaServidor')}>
                        <span>
                            <MDButton size="small" variant="gradient" color="success" disabled>
                                {t('integracoes.faleComSuporte')}
                            </MDButton>
                        </span>
                    </Tooltip>
                ) : native && NATIVE_PAGES[item.key] ? (
                    <MDButton size="small" variant="gradient" color="success" onClick={() => navigate(NATIVE_PAGES[item.key])}>
                        {t('integracoes.abrir')}
                    </MDButton>
                ) : native ? (
                    // Nativa que depende do servidor, como a IA sem chave, chega sem o selo de configurada.
                    <Typography variant="caption" sx={{ color: cc.textMuted }}>
                        {item.configured
                            ? t('integracoes.semConfiguracao')
                            : t('integracoes.naoAtivadaNoServidor')}
                    </Typography>
                ) : (
                    <MDButton
                        size="small"
                        variant="gradient"
                        color={item.configured ? 'dark' : 'success'}
                        disabled={unavailable}
                        onClick={onConfigure}
                    >
                        {unavailable
                            ? t('integracoes.indisponivel')
                            : item.configured
                                ? t('integracoes.gerenciar')
                                : t('integracoes.configurar')}
                    </MDButton>
                )}
            </Box>
        </Box>
    );
};
